/**
 * Shop details (T11-44): the facts of the shop typed once. Their home is the shop's details record
 * (T01-16, cms `GET/PATCH /v1/admin/settings/store` — header, receipts, 〒, カナ, logo); saving
 * also writes them where the law and the e-mails show them — the drafts of three cms legal pages
 * (T09-08) and the e-mail footer of notification-service (T12-33). Before the record was ever
 * saved the form starts from those drafts:
 *
 * | fact | 特商法 (`tokushoho`) | 古物 notice (`kobutsu`) | contact | e-mail footer |
 * |---|---|---|---|---|
 * | shop name ja/en | business_name | licensee_name | — | shopName |
 * | representative ja/en | representative | — | — | — |
 * | address ja/en | address | — | address | addressLine |
 * | phone (+ hours ja/en) | phone (shared), phone_hours | — | phone, hours | phone |
 * | e-mail | email (shared) | — | email | email |
 * | website | url (shared) | notified_url | — | — |
 * | 公安委員会 ja/en | — | commission_name | — | — |
 * | 古物商許可番号 | — | license_number (shared) | — | kobutsuPermitNo |
 *
 * Pure, unit tested.
 */
import type { StoreSettingsOut, StoreSettingsPatch } from "./storeSettingsApi";
import type { PolicyFields } from "./tokushoho/items";

export const FACT_LANGS = ["ja", "en"] as const;
export type FactLang = (typeof FACT_LANGS)[number];

export interface StoreFacts {
  shopName: Record<FactLang, string>;
  /** Shop name in katakana (フリガナ). */
  nameKana: string;
  /** 〒 as 123-4567. */
  postalCode: string;
  representative: Record<FactLang, string>;
  address: Record<FactLang, string>;
  phone: string;
  phoneHours: Record<FactLang, string>;
  email: string;
  website: string;
  commission: Record<FactLang, string>;
  licenseNumber: string;
  /** Logo picture of the media library (`null` = none). */
  logoAssetId: string | null;
}

const pair = (): Record<FactLang, string> => ({ ja: "", en: "" });

export function emptyFacts(): StoreFacts {
  return {
    shopName: pair(),
    nameKana: "",
    postalCode: "",
    representative: pair(),
    address: pair(),
    phone: "",
    phoneHours: pair(),
    email: "",
    website: "",
    commission: pair(),
    licenseNumber: "",
    logoAssetId: null,
  };
}

const POSTAL_PREFIX = /^〒?\s*(\d{3})-?(\d{4})\s+/;

/** The shop's details record → the form. */
export function factsFromStore(settings: StoreSettingsOut): StoreFacts {
  const lang = (value: { ja?: string; en?: string } | undefined): Record<FactLang, string> => ({
    ja: value?.ja ?? "",
    en: value?.en ?? "",
  });
  return {
    shopName: lang(settings.name),
    nameKana: settings.nameKana ?? "",
    postalCode: settings.postalCode ?? "",
    representative: lang(settings.representative),
    address: lang(settings.address),
    phone: settings.phone ?? "",
    phoneHours: lang(settings.phoneHours),
    email: settings.email ?? "",
    website: settings.website ?? "",
    commission: lang(settings.kobutsu?.commission),
    licenseNumber: settings.kobutsu?.licenseNumber ?? "",
    logoAssetId: settings.logoAssetId ?? null,
  };
}

/** The form → `PATCH /v1/admin/settings/store` (every fact; the service keeps what did not change). */
export function storePatchFrom(facts: StoreFacts): StoreSettingsPatch {
  const trim = (value: Record<FactLang, string>) => ({ ja: value.ja.trim(), en: value.en.trim() });
  return {
    name: trim(facts.shopName),
    nameKana: facts.nameKana.trim(),
    postalCode: normalizePostal(facts.postalCode),
    representative: trim(facts.representative),
    address: trim(facts.address),
    phone: facts.phone.trim(),
    phoneHours: trim(facts.phoneHours),
    email: facts.email.trim(),
    website: facts.website.trim(),
    kobutsu: { commission: trim(facts.commission), licenseNumber: facts.licenseNumber.trim() },
    logoAssetId: facts.logoAssetId,
  };
}

/** `1500001` / `〒150-0001` → `150-0001` (anything else as typed). */
export function normalizePostal(value: string): string {
  const match = /^〒?\s*(\d{3})-?(\d{4})$/.exec(value.normalize("NFKC").trim());
  return match ? `${match[1] ?? ""}-${match[2] ?? ""}` : value.trim();
}

/** The address as the legal pages show it: 〒 first in Japanese, last in English. */
export function addressWithPostal(facts: StoreFacts, lang: FactLang): string {
  const address = facts.address[lang].trim();
  const postal = normalizePostal(facts.postalCode);
  if (!postal || !address) {
    return address;
  }
  return lang === "ja" ? `〒${postal} ${address}` : `${address} ${postal}`;
}

const PLACEHOLDER = /\[\[[^[\]]{1,120}\]\]/;

function read(fields: PolicyFields | undefined, name: string, lang: string): string {
  const value = fields?.shared?.[name] ?? fields?.[lang]?.[name] ?? "";
  return PLACEHOLDER.test(value) ? "" : value;
}

function first(...values: string[]): string {
  return values.find((v) => v.trim() !== "") ?? "";
}

export function factsFrom(
  tokushoho: PolicyFields | undefined,
  kobutsu: PolicyFields | undefined,
  contact: PolicyFields | undefined,
): StoreFacts {
  const facts = emptyFacts();
  for (const lang of FACT_LANGS) {
    facts.shopName[lang] = first(
      read(tokushoho, "business_name", lang),
      read(kobutsu, "licensee_name", lang),
    );
    facts.representative[lang] = read(tokushoho, "representative", lang);
    facts.address[lang] = first(read(tokushoho, "address", lang), read(contact, "address", lang));
    facts.phoneHours[lang] = first(
      read(tokushoho, "phone_hours", lang),
      read(contact, "hours", lang),
    );
    facts.commission[lang] = read(kobutsu, "commission_name", lang);
  }
  facts.phone = first(read(tokushoho, "phone", "ja"), read(contact, "phone", "ja"));
  facts.email = first(read(tokushoho, "email", "ja"), read(contact, "email", "ja"));
  facts.website = first(read(tokushoho, "url", "ja"), read(kobutsu, "notified_url", "ja"));
  facts.licenseNumber = read(kobutsu, "license_number", "ja");
  const postal = POSTAL_PREFIX.exec(facts.address.ja);
  if (postal) {
    facts.postalCode = `${postal[1] ?? ""}-${postal[2] ?? ""}`;
    facts.address.ja = facts.address.ja.slice(postal[0].length);
    const tail = ` ${facts.postalCode}`;
    if (facts.address.en.endsWith(tail)) {
      facts.address.en = facts.address.en.slice(0, -tail.length);
    }
  }
  return facts;
}

type Scoped = Record<string, Record<string, string>>;

function put(target: Scoped, scope: string, name: string, value: string) {
  target[scope] = { ...(target[scope] ?? {}), [name]: value.trim() };
}

/**
 * The policy `fields` after writing the facts into them (other fields untouched). Shared facts go
 * to `shared`; a fact left empty clears the box (the owner removed it).
 */
export function withFacts(
  type: "tokushoho" | "kobutsu" | "contact",
  fields: Readonly<Record<string, Readonly<Record<string, string>>>>,
  facts: StoreFacts,
): Scoped {
  const out: Scoped = {};
  for (const [scope, values] of Object.entries(fields)) {
    out[scope] = { ...values };
  }
  for (const lang of FACT_LANGS) {
    if (type === "tokushoho") {
      put(out, lang, "business_name", facts.shopName[lang]);
      put(out, lang, "representative", facts.representative[lang]);
      put(out, lang, "address", addressWithPostal(facts, lang));
      put(out, lang, "phone_hours", facts.phoneHours[lang]);
    } else if (type === "kobutsu") {
      put(out, lang, "licensee_name", facts.shopName[lang]);
      put(out, lang, "commission_name", facts.commission[lang]);
    } else {
      put(out, lang, "address", addressWithPostal(facts, lang));
      put(out, lang, "hours", facts.phoneHours[lang]);
    }
  }
  if (type === "tokushoho") {
    put(out, "shared", "phone", facts.phone);
    put(out, "shared", "email", facts.email);
    put(out, "shared", "url", facts.website);
  } else if (type === "kobutsu") {
    put(out, "shared", "license_number", facts.licenseNumber);
    put(out, "shared", "notified_url", facts.website);
  } else {
    put(out, "shared", "phone", facts.phone);
    put(out, "shared", "email", facts.email);
  }
  return out;
}

/** The e-mail footer part of `PATCH /admin/settings/notifications` (`footer` merges field by field). */
export function footerFrom(facts: StoreFacts) {
  const names = (value: Record<FactLang, string>) =>
    Object.fromEntries(FACT_LANGS.filter((l) => value[l].trim()).map((l) => [l, value[l].trim()]));
  return {
    shopName: names(facts.shopName),
    addressLine: names({ ja: addressWithPostal(facts, "ja"), en: addressWithPostal(facts, "en") }),
    phone: facts.phone.trim() || null,
    email: facts.email.trim() || null,
    kobutsuPermitNo: facts.licenseNumber.trim() || null,
  };
}

export type FactProblem =
  "email" | "phone" | "website" | "postalCode" | "nameKana" | "licenseNumber";

/** The 古物商許可番号 without 第 / 号 / spaces / hyphens — the same rule as the server gate
 * (shop_common.shop_status: NFKC, then at least 6 digits; the real length is ยังไม่ยืนยัน). */
export function licenseDigits(value: string): string {
  return value.normalize("NFKC").replace(/[第号號\s\-－ー‐−]/gu, "");
}

export function factProblems(facts: StoreFacts): FactProblem[] {
  const out: FactProblem[] = [];
  if (facts.email.trim() && !/^[^@\s<>"']+@[^@\s<>"']+\.[^@\s<>"']+$/.test(facts.email.trim())) {
    out.push("email");
  }
  if (facts.phone.trim() && !/^[+0-9()\-\s.]{6,40}$/.test(facts.phone.trim())) {
    out.push("phone");
  }
  if (facts.website.trim() && !/^https?:\/\/\S+$/.test(facts.website.trim())) {
    out.push("website");
  }
  if (facts.postalCode.trim() && !/^〒?\s*\d{3}-?\d{4}$/.test(facts.postalCode.trim())) {
    out.push("postalCode");
  }
  if (facts.licenseNumber.trim() && !/^\d{6,}$/.test(licenseDigits(facts.licenseNumber))) {
    out.push("licenseNumber");
  }
  if (facts.nameKana.trim() && !/^[\u30A0-\u30FF\u3000 ・ー]+$/.test(facts.nameKana.trim())) {
    out.push("nameKana");
  }
  return out;
}

export function sameFacts(a: StoreFacts, b: StoreFacts): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}
